// Imports removed for lazy loading
import { getFirestore, doc, getDoc, collectionGroup, query, where, getDocs, documentId } from "firebase/firestore";
import { app, analytics } from "./firebase-config.js";
const db = getFirestore(app);
import { logEvent } from "firebase/analytics";

import { Layout } from './components/layout/Layout.js';
import { locale } from './services/LocaleService.js';

export class Router {
    // export class Router { ... }
    // We keep the class.
    constructor(routes) {
        this.routes = routes;

        const appRoot = document.getElementById('app');
        if (!appRoot) console.error("Critical: #app not found");

        // Initialize Layout
        this.layout = new Layout(appRoot);
        this.root = this.layout.getPageContainer();

        if (!this.root) console.error("Critical: Layout failed to provide page container");

        this.mainContainer = this.root;

        // *** Phase 13: Referral Tracking & Retention (Kakao-Proof) ***
        try {
            const search = new URLSearchParams(window.location.search);
            const utmSource = search.get('utm_source');
            const refCode = search.get('ref');
            const redirectPath = search.get('redirect');
            const referrer = document.referrer;

            // 1. Capture Redirect Path (Highest Priority)
            if (redirectPath) {
                // [FIX] Loop Prevention: If we are ALREADY at the redirect path, do not store it.
                // Normalize paths for comparison (remove leading/trailing slashes and #)
                const currentPath = window.location.hash.slice(1).split('?')[0];
                const targetPath = redirectPath.replace(/^[#\/]+/, '').split('?')[0];

                if (currentPath === targetPath) {
                    console.log("[Router] Already at redirect destination. Ignoring.");
                } else {
                    sessionStorage.setItem('auth_redirect', redirectPath);
                    console.log("[Router] Retention Path Captured:", redirectPath);
                }

                // [FIX v1.36.345] Clean URL to prevent Loop on Reload
                try {
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('redirect');
                    window.history.replaceState({}, document.title, newUrl.toString());
                    console.log("[Router] Cleaned 'redirect' param from URL.");
                } catch (e) {
                    console.warn("[Router] Failed to clean URL params", e);
                }
            }

            // 2. Capture Referral Logic
            // Only capture if not already captured (first touch)
            if (!sessionStorage.getItem('referralData')) {
                const data = {};
                if (refCode) {
                    data.source = 'invite_link';
                    data.code = refCode; // Explicit Referral Code
                    data.medium = 'social';
                } else if (utmSource) {
                    data.source = utmSource;
                    data.medium = search.get('utm_medium') || 'unknown';
                    data.campaign = search.get('utm_campaign') || 'unknown';
                } else if (referrer && !referrer.includes(window.location.hostname)) {
                    data.source = 'referrer';
                    data.medium = new URL(referrer).hostname;
                } else {
                    data.source = 'direct';
                    data.medium = 'none';
                }
                sessionStorage.setItem('referralData', JSON.stringify(data));
                console.log("[Marketing] Referral Captured:", data);
            }
        } catch (e) {
            console.warn("[Marketing] Tracking Error:", e);
        }

        window.addEventListener('popstate', () => {
            this.handleRoute(); // Fixed: resolve() -> handleRoute()
        });

        document.body.addEventListener('click', (e) => {
            if (e.target.matches('[data-link]')) {
                e.preventDefault();
                this.navigate(e.target.href);
            }
        });

        window.router = this;
    }

    addRoute(path, componentFn) {
        this.routes[path] = componentFn;
    }

    navigate(path, replace = false) {
        // [Safety] Normalize absolute URLs (e.g. from e.target.href)
        if (path && path.startsWith('http')) {
            try {
                const url = new URL(path);
                path = url.pathname + url.search;
                // If path is just / and hash exists, use hash? 
                // Usually for SPA, href="/" means root.
            } catch (e) { console.warn("Nav path parse failed", e); }
        }

        // [FIX] Escape Hatch: If going Home explicitly, clear any pending redirect to prevent loops
        if (path === '/' || path === '') {
            sessionStorage.removeItem('auth_redirect');
            // Force hash update even if current hash is empty (for mobile drawer reliability)
            if (window.location.hash === '' || window.location.hash === '#/') {
                // Already at Home, but drawer might be stuck. Force re-render or do nothing?
                // Just let it fall through to hash set.
            }
        }

        // [FIX v1.36.473] Prevent Duplicate Navigation (Infinite Loop Fix)
        // If we are already at the target path, do nothing.
        // This prevents the "Destroy -> Re-create" cycle when clicking notifications for current page.
        const currentHash = window.location.hash.replace('#', '');

        // Normalize paths for comparison (remove trailing slashes, decode)
        const normalize = (p) => {
            try {
                return decodeURIComponent(p).replace(/\/$/, '').toLowerCase();
            } catch (e) { return p.replace(/\/$/, '').toLowerCase(); }
        };

        const normPath = normalize(path);
        const normCurrent = normalize(currentHash);

        // Handle root case separately or normalized?
        // path='/admin/content', hashKey='/admin/content'
        if ((normCurrent === normPath) || (normCurrent === '' && normPath === '/')) {
            console.log(`[Router] Already at ${path}. Skipping navigation.`);
            return;
        }

        if (replace) {
            const url = new URL(window.location.href);
            url.hash = path;
            window.history.replaceState(null, '', url.toString());
            this.handleRoute(); // Manually trigger because replaceState doesn't fire hashchange/popstate sometimes
        } else {
            window.location.hash = path;
        }
    }

    loadInitialRoute() {
        this.handleRoute();
    }

    async handleRoute() {
        // Simple regex matching for routes with params (e.g. /series/:id) could go here.
        // For now, exact match or simple includes.

        let path = window.location.hash.slice(1);

        // [SEO] Update Meta Tags based on current locale
        // This ensures the page title and description are localized
        locale.updateMeta({
            title: 'seo.default_title',
            description: 'seo.default_desc'
        });

        // [ANALYTICS] Log Page View with Locale
        try {
            if (analytics) {
                logEvent(analytics, 'page_view', {
                    page_path: path,
                    language: locale.lang || 'unknown',
                    ref: sessionStorage.getItem('referralData') ? 'referral' : 'direct'
                });
                console.log(`[Analytics] Page View: ${path} (${locale.lang})`);
            }
        } catch (e) {
            console.warn("[Analytics] Log Failed:", e);
        }

        // [CRITICAL FIX v1.36.356] PWA/ServiceWorker Conflict Resolver
        // If the App loads on a non-root pathname (e.g. /share/...), the SW likely served index.html.
        // This causes the Router to stick to the dirty pathname, breaking "Home" navigation.
        // We must Force-Convert this to a Hash-based URL at the Root.
        if (window.location.pathname.length > 1 && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/__/') && !window.location.pathname.includes('.')) {
            console.log("[Router] Dirty Pathname Detected. Sanitizing...", window.location.pathname);
            const cleanUrl = window.location.origin + '/#' + window.location.pathname + window.location.search;
            window.location.replace(cleanUrl);
            return; // Stop processing to allow reload
        }

        if (!path || path === '/') {
            // [SEO FIX] Fallback to pathname if hash is empty/root
            // This allows direct access to /user/123 without redirecting to /#/user/123
            // BUT only if we haven't already sanitized above.
            path = window.location.pathname === '/' ? '/' : path;
        }
        if (path === '/' || path === '') path = '/'; // Normalize root

        // Remove query params for matching
        // Remove query params for matching, but keep them in window.location logic if needed
        // Fix: Allow query params to persist in hash so pages can read them
        // Remove query params for matching
        if (path.includes('?')) {
            path = path.split('?')[0];
        }

        // [CRITICAL FIX] Home always clears redirect to prevent "Trap"
        if (path === '/' || path === '') {
            sessionStorage.removeItem('auth_redirect');
        }

        // [FIX] Loop Prevention: Check if we reached the pending redirect
        const pendingRedirect = sessionStorage.getItem('auth_redirect');
        if (pendingRedirect) {
            const targetPath = pendingRedirect.replace(/^[#\/]+/, '').split('?')[0];
            const currentPath = path.replace(/^[#\/]+/, '');

            if (currentPath === targetPath) {
                console.log("[Router] Reached Pending Redirect Destination. Clearing.");
                sessionStorage.removeItem('auth_redirect');
            }
        }

        // [SAFETY] Global Click Interceptor (One-time)
        // If user physically clicks ANYTHING, we assume they are taking control.
        // We clear the auto-redirect to prevent "Traps".
        if (!this._hasClickSafety) {
            document.addEventListener('click', () => {
                if (sessionStorage.getItem('auth_redirect')) {
                    console.log("[Router] User Interaction detected. Clearing pending redirect.");
                    sessionStorage.removeItem('auth_redirect');
                }
            }, { capture: true, once: false }); // Keep active
            this._hasClickSafety = true;
        }

        // *** PHASE 12: PERFORMANCE & CLEANUP ***
        // Detect and destroy previous component to prevent memory leaks (e.g. Firebase listeners)
        if (this.root.firstElementChild) {
            const oldComponent = this.root.firstElementChild;
            // Standard cleanup 'destroy' or legacy '_firestoreUnsub'
            if (typeof oldComponent.destroy === 'function') {
                console.log("[Router] Invoking destroy() on previous component");
                oldComponent.destroy();
            } else if (typeof oldComponent._firestoreUnsub === 'function') {
                console.log("[Router] Invoking _firestoreUnsub() on previous component");
                oldComponent._firestoreUnsub();
            }
        }

        // *** BGM/TTS Cleanup (v0.132) ***
        if (window.activeViewerController) {
            console.log('[Router] Cleaning up active viewer controller...');
            window.activeViewerController.destroy();
            window.activeViewerController = null;
        }

        // *** Header Cleanup (v0.639) ***
        if (window.activeViewerHeader) {
            console.log('[Router] Cleaning up active viewer header...');
            window.activeViewerHeader.destroy();
            window.activeViewerHeader = null;
        }

        // *** CLEANUP: Remove Webtoon Viewer Global Styles (v0.097) ***
        // Fix for "Sticky Dark Mode" issue where viewer styles persisted to home.
        const viewerStyle = document.getElementById('webtoon-viewer-styles');
        if (viewerStyle) viewerStyle.remove();

        // --- Navbar Visibility (Immediate) ---
        // Handle this BEFORE loading component to prevent layout shifts/z-index issues
        const shouldHideNavbar = path === '/login' || path.startsWith('/studio');
        const appHeader = document.getElementById('app-header');
        if (appHeader) {
            if (shouldHideNavbar) {
                appHeader.classList.add('-translate-y-full');
            } else {
                appHeader.classList.remove('-translate-y-full');
            }
        }

        // Auth Check
        const isAuthRoute = path === '/login';
        if (!isAuthRoute && (!window.user || !window.user.emailVerified)) {
            // Let auth listener in auth.js handle redirect?
            // Or force it here if strictly null
            // Let auth listener handle redirect
        }

        // --- Render Loading ---
        // REPLACE: Simple Text Loader -> SplashScreen Overlay
        let splash = null;
        const initialLoader = document.getElementById('initial-loader');

        // Only show new splash if initial loader is effectively gone/null
        if (!initialLoader || initialLoader.style.pointerEvents === 'none') {
            // Import SplashScreen dynamically if possible or assume static import? 
            // Since Router is main entry, let's look for class or import it.
            // We will add import at top of file separately.
            // Assuming SplashScreen is available in scope or we import it here dynamically?
            // Dynamic import is safer given the existing structure.
        }

        // [FIX] Ensure all modals are closed when navigating
        try {
            const { ModalManager } = await import('./utils/ModalManager.js');
            ModalManager.closeAll();
        } catch (e) { console.warn("Router: Modal cleanup failed", e); }

        // We can't easily add top-level import with replace_file_content mid-file.
        // So we will use dynamic import inside handleRoute.

        let SplashScreenClass = null;
        try {
            const module = await import('./components/auth/SplashScreen.js');
            SplashScreenClass = module.SplashScreen;

            if (!initialLoader) {
                splash = new SplashScreenClass();
                splash.render();
            }
        } catch (e) {
            console.warn("Failed to load Splash", e);
            this.root.innerHTML = '<div class="text-center py-20">Loading...</div>';
        }

        // --- Route Matching ---
        let component = null;

        // Custom Regex Matching
        const seriesDetailMatch = path.match(/^\/series\/([^/]+)$/);
        // Standard Webtoon/Universal Route (Supports singular 'episode' and plural 'episodes')
        const episodeMatch = path.match(/^\/series\/([^/]+)\/episodes?\/([^/]+)$/);
        // Explicit Webnovel Route (v4.96)
        const webnovelMatch = path.match(/^\/webnovel\/([^/]+)\/([^/]+)$/);

        // [FIX v1.36.213] Client-Side Share Link Bypass
        // Handles /#/share/novel/123 -> /novel/123
        const shareMatch = path.match(/^\/share\/([^/]+)\/([^/]+)$/);

        const editorNewMatch = path.match(/^\/studio\/series\/([^/]+)\/editor$/);
        const editorEditMatch = path.match(/^\/studio\/series\/([^/]+)\/editor\/([^/]+)$/);
        const userMatch = path.match(/^\/user\/([^/]+)$/);

        // [SEO Fix] Support SEO/Share Routes
        const novelInfoMatch = path.match(/^\/novel\/([^/]+)$/);
        const webtoonInfoMatch = path.match(/^\/webtoon\/([^/]+)$/);

        try {
            if (path === '/' || path === '') {
                const { HomePage } = await import('./pages/home.js');
                component = await HomePage();
            } else if (path === '/webtoon') {
                const { GalleryPage } = await import('./pages/gallery.js');
                component = await GalleryPage('webtoon');
            } else if (path === '/novel') {
                const { GalleryPage } = await import('./pages/gallery.js');
                component = await GalleryPage('webnovel');
            } else if (path === '/library') {
                const { MyPage } = await import('./pages/mypage.js');
                component = await MyPage();

            } else if (path === '/library/recommendation') {
                const { RecommendationPage } = await import('./pages/recommendation-page.js');
                component = await RecommendationPage();
            } else if (path.match(/^\/davinci\/series\/([^/]+)$/)) {
                // Episode List
                const match = path.match(/^\/davinci\/series\/([^/]+)$/);
                const { DaVinciEpisodeList } = await import('./lab/davinci/pages/DaVinciEpisodeList.js');
                component = await DaVinciEpisodeList(match[1]);
            } else if (path.match(/^\/davinci\/series\/([^/]+)\/episode\/([^/]+)$/)) {
                // Episode Editor
                const match = path.match(/^\/davinci\/series\/([^/]+)\/episode\/([^/]+)$/);
                const { DaVinciEditorPage } = await import('./lab/davinci/pages/DaVinciEditorPage.js');
                component = await DaVinciEditorPage({ seriesId: match[1], episodeId: match[2] });
            } else if (path.startsWith('/share/')) {
                // [FIX v1.36.359] Robust Share Link Parsing (Split-based)
                // Format: /share/{type}/{seriesId} or /share/{type}/{seriesId}/ep/{episodeId}

                // Remove potential trailing slash
                const cleanPath = path.endsWith('/') ? path.slice(0, -1) : path;
                const parts = cleanPath.split('/');
                // parts: ['', 'share', 'type', 'seriesId', 'ep', 'episodeId']

                // Safety check
                if (parts.length < 4) {
                    console.warn("[Router] Invalid Share Link:", path);
                    this.navigate('/', true);
                    return;
                }

                let type = parts[2];
                const seriesId = parts[3];
                let episodeId = null;

                // [FIX v1.36.363] Robust Segment Scanning
                // Iterate to find 'ep' key, handling potential double slashes or shifted indices
                // e.g. /share/novel/123//ep/456
                const epIndex = parts.indexOf('ep');
                if (epIndex !== -1 && parts.length > epIndex + 1) {
                    // [FIX v1.36.360] Sanitize Episode ID (Remove Query Params/Hash)
                    let candidateId = parts[epIndex + 1].split(/[?#]/)[0];

                    // [FIX v1.36.361] Ignore Metadata IDs (Legacy Link Protection)
                    if (candidateId && !candidateId.includes('geo_marketing') && !candidateId.includes('seo_analysis')) {
                        episodeId = candidateId;
                    }
                }

                console.log(`[Router] Share Logic: Type=${type}, Series=${seriesId}, Episode=${episodeId}`);

                // Normalize Type
                if (type === 'davinci' || type === 'webnovel') type = 'novel';

                if (episodeId) {
                    // [FIX v1.36.362] Universal Deep Link Routing
                    // User confirmed valid content address is: /series/{id}/episode/{id}
                    // This route handles both Webtoon and Novel viewers via 'episodeMatch' logic.
                    // We avoid /webnovel/... specific route to prevent fragmentation.
                    this.navigate(`/series/${seriesId}/episode/${episodeId}`, true);
                } else {
                    // Series Link -> Detail Page
                    this.navigate(`/${type}/${seriesId}`, true);
                }
                return;
            } else if (path === '/davinci' || path === '/studio/lab/davinci') { // [FIX] Combined routes
                console.log("[Router] Loading DaVinci Dashboard...");
                try {
                    // Time-boxed Import
                    const loadPromise = import('./lab/davinci/pages/DaVinciDashboard.js');
                    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Loading Timed Out")), 5000));

                    const module = await Promise.race([loadPromise, timeoutPromise]);
                    component = await module.DaVinciDashboard();
                } catch (e) {
                    console.error("[Router] DaVinci Load Failed:", e);
                    throw e; // Handled by outer router catch
                }
            } else if (path.startsWith('/davinci/edit/')) {
                // Legacy Editor (Deprecate or Redirect?)
                // Assuming legacy edit was project level. 
                // Let's redirect to dashboard for safety as legacy projects might need migration.
                window.router.navigate('/davinci');
                return;
            } else if (path === '/studio') {
                const { AuthorStudioPage } = await import('./pages/author-studio.js');
                component = await AuthorStudioPage();
            } else if (path === '/admin') {
                // [FIX] Handle Notification Link /admin -> Default to Content
                window.router.navigate('/admin/content', true);
                return;
            } else if (path.match(/^\/viewer\/([^/]+)$/)) {
                // [FIX] Legacy Notification Link /viewer/:episodeId -> /series/:seriesId/episode/:episodeId
                const match = path.match(/^\/viewer\/([^/]+)$/);
                const episodeId = match[1];

                // We need to find the seriesId. 
                // Since this is a router/navigation fix, we can do a quick lookup.
                // However, episodes are in subcollections: series/{seriesId}/episodes/{episodeId}
                // We cannot query subcollections easily by ID without Collection Group.
                // Try Collection Group Query for 'episodes' where documentId == episodeId?
                // Actually, 'episodes' collection group query is possible if indexed.
                // OR, we assume we might not find it easily.
                // BUT, notifications usually carry metadata. The Router doesn't receive metadata.

                // Strategy: Try to find the episode in standard locations?
                // Realistically, without seriesId, we can't construct the URL efficiently.
                // ERROR: The backend sends /viewer/:id. This is a BUG in backend.
                // WORKAROUND: Show "Redirecting..." and try to find it via Collection Group.

                console.log("[Router] Legacy Viewer Link detected. Attempting resolution...");
                this.root.innerHTML = '<div class="text-center py-20">Looking for episode...</div>';

                try {
                    const q = query(collectionGroup(db, 'episodes'), where('id', '==', episodeId), where('status', '==', 'published'));
                    const querySnapshot = await getDocs(q);
                    if (!querySnapshot.empty) {
                        const doc = querySnapshot.docs[0];
                        const seriesId = doc.ref.parent.parent.id;
                        this.navigate(`/series/${seriesId}/episode/${episodeId}`, true);
                        return;
                    } else {
                        throw new Error("Episode not found");
                    }
                } catch (e) {
                    console.warn("Episode lookup failed", e);
                    this.root.innerHTML = '<div class="text-center py-20">Episode not found.</div>';
                }
                return;

            } else if (path === '/admin/content') {
                const { AdminDashboardPage } = await import('./pages/admin-dashboard.js');
                component = await AdminDashboardPage();
            } else if (path === '/admin/config') {
                const { AdminConfigPage } = await import('./pages/admin/AdminConfigPage.js');
                component = await AdminConfigPage();
            } else if (path === '/admin/popups') {
                const { AdminPopupsPage } = await import('./pages/admin/AdminPopupsPage.js');
                component = await AdminPopupsPage();
            } else if (path === '/admin/docs') {
                const { AdminDocsPage } = await import('./pages/admin/AdminDocsPage.js');
                component = await AdminDocsPage();
            } else if (path === '/admin/reports') {
                const { AdminReportsPage } = await import('./pages/admin/AdminReportsPage.js');
                component = await AdminReportsPage();
            } else if (path === '/admin/analytics') {
                const { AdminAnalyticsPage } = await import('./pages/admin/AdminAnalyticsPage.js');
                component = await AdminAnalyticsPage();
            } else if (path === '/gallery') {
                const { GalleryPage } = await import('./pages/gallery.js');
                component = await GalleryPage();
            } else if (path === '/mypage') {
                const { MyPage } = await import('./pages/mypage.js');
                component = await MyPage();
            } else if (path === '/charging-station' || path === '/store') { // [Phase 3] Star Charging Station
                const { StarChargePage } = await import('./pages/StarChargePage.js');
                // Create a container wrapper to pass to the function if it expects one 
                // or the function returns a node. 
                // Based on StarChargePage code: export const StarChargePage = async (container) => { ... }
                // It modifies the container. 
                // But Router expects a component to be returned (Node) usually.
                // Let's adapt: Create a div, pass it, await, then return the div.
                const div = document.createElement('div');
                await StarChargePage(div);
                component = div;
            } else if (userMatch) {
                const { PublicProfilePage } = await import('./pages/public-profile.js');
                component = await PublicProfilePage(userMatch[1]);
            } else if (path === '/login') {
                const { LoginPage } = await import('./pages/login.js');
                component = await LoginPage();

                // Hide Navbar
                const appHeader = document.getElementById('app-header');
                if (appHeader) appHeader.classList.add('-translate-y-full');

            } else if (path === '/welcome') {
                const { WelcomePage } = await import('./pages/WelcomePage.js');
                component = await WelcomePage();
                const appHeader = document.getElementById('app-header');
                if (appHeader) appHeader.classList.add('-translate-y-full');

            } else if (path === '/intro') {
                const { IntroPage } = await import('./pages/IntroPage.js');
                component = await IntroPage();
                // Hide Header
                const appHeader = document.getElementById('app-header');
                if (appHeader) appHeader.classList.add('-translate-y-full');

            } else if (webnovelMatch) {
                // *** FIX v0.105: Explicitly remove global padding ***
                const { WebnovelViewerPage } = await import('./pages/webnovel-viewer.js');
                component = await WebnovelViewerPage(webnovelMatch[1], webnovelMatch[2]);

            } else if (episodeMatch) {
                // ... (Legacy Universal Logic)
                const seriesId = episodeMatch[1];
                const epId = episodeMatch[2];
                try {
                    const sDoc = await getDoc(doc(db, "series", seriesId));
                    if (sDoc.exists()) {
                        const type = sDoc.data().type;
                        if (type === 'webnovel') {
                            const { WebnovelViewerPage } = await import('./pages/webnovel-viewer.js');
                            component = await WebnovelViewerPage(seriesId, epId);
                        } else if (type === 'davinci') {
                            // [FIX] Route to DaVinci Viewer
                            const { DaVinciViewerPage } = await import('./lab/davinci/pages/davinci-viewer.js');
                            component = await DaVinciViewerPage(seriesId, epId);
                        } else {
                            // Default: Webtoon
                            const { WebtoonViewerPage } = await import('./pages/webtoon-viewer.js');
                            component = await WebtoonViewerPage(seriesId, epId);
                        }
                    } else {
                        // Fallback (e.g. Doc Deleted) -> Webtoon
                        const { WebtoonViewerPage } = await import('./pages/webtoon-viewer.js');
                        component = await WebtoonViewerPage(seriesId, epId);
                    }
                } catch (e) {
                    console.error(e);
                    const { WebtoonViewerPage } = await import('./pages/webtoon-viewer.js');
                    component = await WebtoonViewerPage(seriesId, epId);
                }

            } else if (editorNewMatch) {
                const { EpisodeEditorPage } = await import('./pages/episode-editor.js');
                component = await EpisodeEditorPage({ id: editorNewMatch[1] });
            } else if (editorEditMatch) {
                const { EpisodeEditorPage } = await import('./pages/episode-editor.js');
                component = await EpisodeEditorPage({ id: editorEditMatch[1], episodeId: editorEditMatch[2] });
            } else if (path === '/plaza') {
                const { PlazaPage } = await import('./pages/PlazaPage.js');
                component = await PlazaPage();
            } else if (path === '/settings') {
                const { SettingsPage } = await import("./pages/SettingsPage.js");
                component = await SettingsPage();
            } else if (path === '/dashboard') {
                const { UserDashboardPage } = await import("./pages/user-dashboard.js");
                component = await UserDashboardPage();
            } else if (path === '/banned') {
                const { BannedPage } = await import("./pages/BannedPage.js");
                component = await BannedPage();

                // Hide Header for full immersion
                const appHeader = document.getElementById('app-header');
                if (appHeader) appHeader.classList.add('-translate-y-full');

            } else if (seriesDetailMatch) {
                const { SeriesDetailPage } = await import('./pages/series-detail.js');
                component = await SeriesDetailPage(seriesDetailMatch[1]);
            } else if (novelInfoMatch) {
                const { SeriesDetailPage } = await import('./pages/series-detail.js');
                component = await SeriesDetailPage(novelInfoMatch[1]);
            } else if (webtoonInfoMatch) {
                const { SeriesDetailPage } = await import('./pages/series-detail.js');
                component = await SeriesDetailPage(webtoonInfoMatch[1]);
            } else if (path.match(/^\/viewer\/davinci\/series\/([^/]+)\/episode\/([^/]+)$/)) {
                // New Cine-Novel Viewer (Series + Episode)
                const match = path.match(/^\/viewer\/davinci\/series\/([^/]+)\/episode\/([^/]+)$/);
                const { DaVinciViewerPage } = await import('./lab/davinci/pages/davinci-viewer.js');
                component = await DaVinciViewerPage(match[1], match[2]);
            } else if (path.match(/^\/viewer\/davinci\/([^/]+)$/)) {
                // Legacy Project Viewer
                const match = path.match(/^\/viewer\/davinci\/([^/]+)$/);
                const { DaVinciViewerPage } = await import('./lab/davinci/pages/davinci-viewer.js');
                component = await DaVinciViewerPage(match[1]);
            } else {
                component = null; // 404 handled below
            }
        } catch (loadError) {
            console.error("Component Load Error:", loadError);
            if (splash) splash.destroy(); // Ensure splash is gone on error

            // If initial loader exists, remove it so user sees error
            if (initialLoader) initialLoader.remove();

            this.root.innerHTML = `
                <div class="px-6 py-20 text-center text-red-500">
                    <p class="font-bold">Failed to load content</p>
                    <p class="text-xs mt-2">${loadError.message}</p>
                    <button onclick="window.location.reload()" class="mt-4 px-4 py-2 bg-gray-200 rounded text-sm">Retry</button>
                </div>
             `;
            return;
        }

        // Check for 404
        if (!component && !this.root.hasChildNodes()) {
            console.warn('[Router] 404 Component Not Found');
            if (splash) splash.destroy();
            if (initialLoader) initialLoader.remove();

            if (!path.startsWith('/login')) {
                this.root.innerHTML = '<div class="text-center py-20">404 Not Found</div>';
            }
            return;
        }

        // Render Component
        try {
            console.log('[Router] Rendering component...', component);
            // Navbar Handling (Moved into early execution)

            // Clear and Append
            if (component) {
                this.root.innerHTML = '';
                this.root.appendChild(component);
                console.log('[Router] Component appended successfully.');

                // Cleanup Loading Screens
                if (splash) {
                    splash.destroy();
                }
                if (initialLoader) {
                    // Fade out initial loader
                    initialLoader.classList.add('opacity-0', 'pointer-events-none');
                    setTimeout(() => {
                        if (initialLoader.parentNode) initialLoader.parentNode.removeChild(initialLoader);
                    }, 1000);
                }

            } else if (!this.root.hasChildNodes()) {
                this.root.innerHTML = '<div class="text-center py-20">404 Not Found</div>';
                if (splash) splash.destroy();
                if (initialLoader) initialLoader.remove();
            }

        } catch (renderError) {
            console.error("Render Error:", renderError);
            if (splash) splash.destroy();
            if (initialLoader) initialLoader.remove();

            this.root.innerHTML = `
                <div class="px-6 py-10 text-center text-red-600">
                    <p class="font-bold">Error Loading Page</p>
                    <p class="text-xs mt-2">${renderError.message}</p>
                </div>
             `;
        }
    }

}

window.router = new Router();
// router.addRoute is deprecated in this new dynamic version,
// we use the if/else in handleRoute for simplicity in this artifact.
// In Phase 3 we can make a proper match map.
